import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ClipboardCheck, Pause, Play, RotateCcw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionHeading } from './SectionHeading';

const DEMOS = {
  offline: {
    label: 'An offline sale',
    steps: [
      ['The connection drops.', 'A powered, configured till uses its previously cached catalogue.'],
      ['The counter keeps moving.', 'The cashier adds available cached items and records a cash sale.'],
      ['The sale is queued.', 'The sale and its reference are stored on the device, awaiting connectivity.'],
      ['Connection restored.', 'Queued sales are sent to the server for reconciliation.'],
      ['Exceptions get attention.', 'Any stock conflict is sent to the manager’s review queue.'],
    ],
    statuses: ['Offline · cached catalogue', 'Offline · cash sale', 'Offline · sale queued', 'Online · reconciling', 'Online · manager review'],
  },
  refill: {
    label: 'A patient refill',
    steps: [
      ['A patient takes the first step.', 'With an internet connection, the patient requests an eligible refill in the portal.'],
      ['The request joins the queue.', 'The pharmacy team sees the request with the information they have permission to access.'],
      ['Staff prioritise the request.', 'The team can flag a waiting request as urgent for operational prioritisation.'],
      ['Review, quote, approval.', 'The pharmacist reviews and costs the request. The patient approves the quote.'],
      ['Ready for the next visit.', 'The team supports collection and updates the record for continuity of care.'],
    ],
    statuses: ['Online · refill requested', 'Online · awaiting review', 'Online · prioritised', 'Online · quote approval', 'Online · collection'],
  },
};

export const InteractiveDemos = () => {
  const [which, setWhich] = useState('offline');
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const demo = DEMOS[which];
  useEffect(() => {
    if (!playing) return undefined;
    const timer = setTimeout(() => {
      const nextStep = Math.min(step + 1, demo.steps.length - 1);
      setStep(nextStep);
      if (nextStep === demo.steps.length - 1) setPlaying(false);
    }, 3500);
    return () => clearTimeout(timer);
  }, [playing, step, demo]);
  const selectStep = (value) => { setPlaying(false); setStep(value); };
  const offline = which === 'offline' && step < 3;

  return (
    <section className="wl-section wl-walkthrough-section" id="demos" aria-labelledby="demos-heading">
      <div className="wl-wrap">
        <div className="wl-heading-row"><SectionHeading id="demos-heading" eyebrow="05 / FOLLOW THE JOURNEY" title="Small steps. A more connected day." />
          <p className="wl-heading-aside" data-testid="demo-illustrative-notice">Illustrative walkthroughs.<br />Not live patient activity or real-time product status.</p></div>
        <div className="wl-demo-tabs" role="group" aria-label="Choose a walkthrough">
          {Object.entries(DEMOS).map(([key, item]) => <Button key={key} variant="ghost" aria-pressed={which === key} className={which === key ? 'is-active' : ''}
            onClick={() => { setWhich(key); selectStep(0); }} data-testid={`demo-tab-${key}`}>{key === 'offline' ? <WifiOff /> : <ClipboardCheck />}{item.label}</Button>)}
        </div>
        <div className="wl-demo" data-testid="demo-stage">
          <ol className="wl-demo-steps">{demo.steps.map(([title], i) => <li key={title} className={i === step ? 'is-current' : ''}>
            <button type="button" aria-current={i === step ? 'step' : undefined} onClick={() => selectStep(i)} data-testid={`demo-step-${i}`}>
              <span>{i < step ? <Check size={16} /> : `0${i + 1}`}</span>{title}<ArrowRight size={16} />
            </button>
          </li>)}</ol>
          <div className="wl-demo-detail">
            <div className="wl-demo-meta"><span>ILLUSTRATIVE SCENARIO</span><span data-testid="demo-step-counter">0{step + 1} / 05</span></div>
            <div className={`wl-demo-symbol ${offline ? 'is-offline' : ''}`} aria-hidden="true">{offline ? <WifiOff size={38} /> : which === 'refill' ? <ClipboardCheck size={38} /> : <Wifi size={38} />}</div>
            <div className="wl-demo-current" aria-live={playing ? 'off' : 'polite'} aria-atomic="true" data-testid="demo-current-description"><h3>{demo.steps[step][0]}</h3><p>{demo.steps[step][1]}</p></div>
            <span className={`wl-demo-status ${offline ? 'is-offline' : ''}`} data-testid="demo-connection-chip">{offline ? <WifiOff size={13} /> : <Wifi size={13} />}Example: {demo.statuses[step]}</span>
            <div className="wl-demo-controls">
              <Button variant="ghost" className="wl-icon-button" aria-label="Previous step" title="Previous step" disabled={step === 0} onClick={() => selectStep(step - 1)} data-testid="demo-previous"><ArrowLeft /></Button>
              <Button variant="ghost" className="wl-demo-play" onClick={() => { if (!playing && step === 4) setStep(0); setPlaying(!playing); }} data-testid="demo-play-toggle">{playing ? <Pause /> : <Play />}{playing ? 'Pause' : 'Play walkthrough'}</Button>
              <Button variant="ghost" className="wl-icon-button" aria-label="Next step" title="Next step" disabled={step === 4} onClick={() => selectStep(step + 1)} data-testid="demo-next"><ArrowRight /></Button>
              <Button variant="ghost" className="wl-icon-button" aria-label="Restart walkthrough" title="Restart walkthrough" onClick={() => selectStep(0)} data-testid="demo-restart"><RotateCcw /></Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};